/**
 * WebSocketManager - Singleton WebSocket connection manager for React
 * 
 * Provides a stable WebSocket connection across the entire application,
 * surviving React Strict Mode's double-mount behavior.
 * 
 * Features:
 * - Single WebSocket instance app-wide
 * - Reference-counted subscribers (doesn't close on individual component unmount)
 * - Debounced close when last subscriber leaves
 * - Exponential backoff reconnection
 * - Event listener system for messages
 * - Subprotocol 'chat' support
 */

import { devLog, devWarn } from '../utils/devLog';

class WebSocketManager {
  constructor() {
    this.ws = null;
    this.connected = false;
    this.authenticated = false;
    this.subscribers = 0;
    this.listeners = new Map(); // { type: Set<callback> }
    this.reconnectTimer = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.closeDebounceTimer = null;
    this.closeDebounceMs = 1000; // Wait 1s before actually closing
    this.clientId = null;
    this.keepaliveTimer = null;
    this.keepaliveInterval = 30000; // 30 seconds
    this.authenticating = false;

    // 로그인이 SPA 내비게이션(풀 리로드 없이)으로 완료되는 경우(OAuth/2FA 등),
    // 이미 익명 상태로 열려 있던 연결이 새 토큰으로 재인증되지 않는 문제를 방지.
    // 로그인 완료 시 dispatch되는 'userDataChanged' 이벤트를 감지해 즉시 재인증 시도.
    if (typeof window !== 'undefined') {
      window.addEventListener('userDataChanged', () => this.authenticateIfNeeded());
    }
  }

  /**
   * Get WebSocket URL based on environment.
   * REACT_APP_WSS_URL / REACT_APP_WS_URL 우선 (HTTPS 페이지는 wss 우선, 혼합 콘텐츠 방지).
   * 없으면 REACT_APP_API_URL에서 wss/ws + /ws 유도.
   * 그것도 없으면 same-origin /ws — 개발 시 setupProxy가 /ws → 로컬 백엔드(기본 3001)로 프록시.
   */
  getUrl() {
    const isSecure = window.location.protocol === 'https:';
    const wss = process.env.REACT_APP_WSS_URL || '';
    const ws = process.env.REACT_APP_WS_URL || '';
    if (isSecure && wss) return wss;
    if (!isSecure && ws) return ws;
    // HTTPS인데 WS_URL만 있으면 ws://는 브라우저가 차단(혼합 콘텐츠) — 동일 호스트에서 TLS 종료되는 경우 wss로 시도
    if (isSecure && ws && /^ws:\/\//i.test(ws)) {
      devWarn(
        '⚠️ [Manager] HTTPS 페이지에서 ws://는 차단될 수 있습니다. REACT_APP_WSS_URL 설정을 권장합니다. wss://로 치환해 시도합니다.'
      );
      return ws.replace(/^ws:/i, 'wss:');
    }

    const apiBase = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
    if (apiBase) {
      const wsBase = apiBase
        .replace(/^https:/i, 'wss:')
        .replace(/^http:/i, 'ws:');
      return `${wsBase}/ws`;
    }

    // 로컬 npm start: HTTP는 백엔드 포트로 직접 연결(setupProxy와 무관하게 동작). HTTPS dev는 same-origin /ws → setupProxy가 3001로 프록시.
    if (process.env.NODE_ENV === 'development') {
      const devPort = process.env.REACT_APP_DEV_BACKEND_PORT || '3001';
      if (isSecure) {
        return `wss://${window.location.host}/ws`;
      }
      return `ws://127.0.0.1:${devPort}/ws`;
    }

    const protocol = isSecure ? 'wss:' : 'ws:';
    const host = window.location.host;
    return `${protocol}//${host}/ws`;
  }

  /**
   * Ensure WebSocket is connected (idempotent)
   */
  ensureConnected() {
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
      devLog('🔌 [Manager] Already connected or connecting');
      return;
    }
    this.connect();
  }

  /**
   * Connect to WebSocket server
   */
  connect() {
    // Cancel any pending close
    if (this.closeDebounceTimer) {
      clearTimeout(this.closeDebounceTimer);
      this.closeDebounceTimer = null;
      devLog('🔌 [Manager] Cancelled pending close');
    }

    const url = this.getUrl();
    devLog('🔌 [Manager] Connecting to:', url);

    try {
      // Create WebSocket with 'chat' subprotocol
      this.ws = new WebSocket(url, 'chat');
      this.setupEventHandlers();
    } catch (error) {
      console.error('❌ [Manager] Failed to create WebSocket:', error);
      this.scheduleReconnect();
    }
  }

  /**
   * Setup WebSocket event handlers
   */
  setupEventHandlers() {
    if (!this.ws) return;

    this.ws.onopen = () => {
      devLog('✅ [Manager] WebSocket connected successfully');
      this.connected = true;
      this.authenticated = false; // Reset on new connection
      this.authenticating = false;
      this.reconnectAttempts = 0;
      this.startKeepalive();

      // Auto-authenticate if credentials available
      const token = localStorage.getItem('auth_token');

      if (token) {
        // Small delay to ensure connection is fully ready
        setTimeout(() => {
          this.authenticateIfNeeded();
        }, 100);
      } else {
        devLog('ℹ️ [Manager] No credentials - anonymous mode');
      }

      this.triggerEvent('open', {});
    };

    this.ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        const messageType = data.type || 'unknown';
        
        // load_node_response 관련 메시지는 상세 로깅
        if (messageType === 'load_node_response' || messageType === 'load_nodes_batch_response') {
          devLog('📥 [Manager] Received node response:', {
            type: messageType,
            requestId: data.requestId,
            success: data.success,
            error: data.error,
            hasNode: !!data.node,
            hasNodes: !!data.nodes
          });
        } else {
          devLog('📩 [Manager] Message received:', messageType);
        }
        
        // Store client ID from init message
        if (data.type === 'init' && data.id) {
          this.clientId = data.id;
          devLog('🆔 [Manager] Client ID:', this.clientId);
        }

        // Handle authenticated confirmation
        if (data.type === 'authenticated') {
          devLog('✅ [Manager] Authenticated as:', data.nickname);
          this.authenticated = true;
          this.authenticating = false;
          this.triggerEvent('authenticated', data);
        }

        this.triggerEvent('message', data);
        
        // Also trigger specific event types
        if (data.type) {
          if (data.type === 'load_node_response' || data.type === 'load_nodes_batch_response') {
            devLog(`🎯 [Manager] Triggering event: ${data.type}`, {
              hasListeners: this.listeners.has(data.type),
              listenerCount: this.listeners.get(data.type)?.size || 0,
              requestId: data.requestId
            });
          }
          this.triggerEvent(data.type, data);
        }
      } catch (error) {
        console.error('❌ [Manager] Error parsing message:', error, {
          rawData: event.data
        });
      }
    };

    this.ws.onclose = (event) => {
      devLog('🔌 [Manager] Connection closed:', event.code, event.reason);
      this.connected = false;
      this.authenticated = false; // Reset authentication state on disconnect
      this.authenticating = false;
      this.ws = null;
      this.triggerEvent('close', event);

      // Auto-reconnect if not a normal close (1000) and we have subscribers
      if (event.code !== 1000 && this.subscribers > 0 && this.reconnectAttempts < this.maxReconnectAttempts) {
        this.scheduleReconnect();
      } else if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        console.error('❌ [Manager] Max reconnection attempts reached');
        this.triggerEvent('maxReconnectAttemptsReached', {});
      }
    };

    this.ws.onerror = (error) => {
      console.error('❌ [Manager] WebSocket error:', error);
      // Normalize error format for consistent handling across all error listeners
      const normalizedError = {
        type: 'error',
        message: error.message || 'WebSocket connection error',
        error: error // Keep original error for debugging
      };
      this.triggerEvent('error', normalizedError);
    };
  }

  /**
   * Schedule reconnection with exponential backoff
   */
  scheduleReconnect() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }

    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
    devLog(`🔄 [Manager] Reconnecting in ${delay}ms... (attempt ${this.reconnectAttempts + 1}/${this.maxReconnectAttempts})`);

    this.reconnectTimer = setTimeout(() => {
      this.reconnectAttempts++;
      this.connect();
    }, delay);
  }

  /**
   * Subscribe (increment reference count and ensure connection)
   */
  subscribe() {
    this.subscribers++;
    devLog(`🔌 [Manager] Subscribe (count: ${this.subscribers})`);
    this.ensureConnected();
    // 이미 연결되어 있는데(예: 로그인 전 다른 화면이 먼저 연결) 아직 인증되지
    // 않았다면(로그인은 SPA 내비게이션으로 완료돼 재인증 기회를 놓친 경우 등)
    // 페이지 접속 시점에 즉시 재인증을 시도해 새로고침 없이도 연결되게 함.
    this.authenticateIfNeeded();
  }

  /**
   * Unsubscribe (decrement reference count and debounce close)
   */
  unsubscribe() {
    this.subscribers = Math.max(0, this.subscribers - 1);
    devLog(`🔌 [Manager] Unsubscribe (count: ${this.subscribers})`);

    // If no more subscribers, schedule a debounced close
    if (this.subscribers === 0) {
      devLog(`🔌 [Manager] No subscribers, scheduling close in ${this.closeDebounceMs}ms`);
      
      if (this.closeDebounceTimer) {
        clearTimeout(this.closeDebounceTimer);
      }

      this.closeDebounceTimer = setTimeout(() => {
        if (this.subscribers === 0) {
          devLog('🔌 [Manager] Closing connection (no subscribers)');
          this.close();
        } else {
          devLog('🔌 [Manager] Close cancelled (new subscribers)');
        }
        this.closeDebounceTimer = null;
      }, this.closeDebounceMs);
    }
  }

  /**
   * Add event listener
   */
  addListener(type, callback) {
    if (!this.listeners.has(type)) {
      this.listeners.set(type, new Set());
    }
    this.listeners.get(type).add(callback);
    devLog(`🔌 [Manager] Added ${type} listener (total: ${this.listeners.get(type).size})`);
  }

  /**
   * Remove event listener
   */
  removeListener(type, callback) {
    if (!this.listeners.has(type)) return;
    
    this.listeners.get(type).delete(callback);
    devLog(`🔌 [Manager] Removed ${type} listener (remaining: ${this.listeners.get(type).size})`);
    
    // Clean up empty sets
    if (this.listeners.get(type).size === 0) {
      this.listeners.delete(type);
    }
  }

  /**
   * Trigger event to all registered listeners
   */
  triggerEvent(type, data) {
    if (!this.listeners.has(type)) {
      if (type === 'load_node_response' || type === 'load_nodes_batch_response') {
        devWarn(`⚠️ [Manager] No listeners registered for ${type}`, {
          availableListeners: Array.from(this.listeners.keys()),
          requestId: data.requestId
        });
      }
      return;
    }

    const listenerCount = this.listeners.get(type).size;
    if (type === 'load_node_response' || type === 'load_nodes_batch_response') {
      devLog(`✅ [Manager] Triggering ${type} to ${listenerCount} listener(s)`, {
        requestId: data.requestId
      });
    }

    this.listeners.get(type).forEach((callback) => {
      try {
        callback(data);
      } catch (error) {
        console.error(`❌ [Manager] Error in ${type} listener:`, error);
      }
    });
  }

  /**
   * Send message through WebSocket
   */
  send(message) {
    if (!this.connected || !this.ws || this.ws.readyState !== WebSocket.OPEN) {
      devWarn('⚠️ [Manager] Cannot send: WebSocket not connected');
      return false;
    }

    try {
      const payload = typeof message === 'string' ? message : JSON.stringify(message);
      this.ws.send(payload);
      devLog('📤 [Manager] Message sent:', message.action || message.type || 'unknown');
      return true;
    } catch (error) {
      console.error('❌ [Manager] Error sending message:', error);
      return false;
    }
  }

  /**
   * Authenticate user on WebSocket connection
   */
  authenticate(token) {
    if (!this.connected || !this.ws || this.ws.readyState !== WebSocket.OPEN) {
      devWarn('⚠️ [Manager] Cannot authenticate: not connected');
      return false;
    }

    try {
      this.authenticating = true;
      this.send({
        type: "authenticate",
        token: token
      });
      devLog('🔐 [Manager] Authentication sent');
      return true;
    } catch (error) {
      console.error('❌ [Manager] Authentication failed:', error);
      this.authenticating = false;
      return false;
    }
  }

  /**
   * Authenticate only if connected, not already authenticated/authenticating,
   * and a token is available. Safe to call redundantly (e.g. on every
   * subscribe() or on 'userDataChanged') to recover a connection that was
   * opened anonymously before login and never got a chance to authenticate
   * (e.g. login completed via SPA navigation without a full page reload).
   */
  authenticateIfNeeded() {
    if (!this.connected || !this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return false;
    }
    if (this.authenticated || this.authenticating) {
      return false;
    }
    const token = localStorage.getItem('auth_token');
    if (!token) {
      return false;
    }
    devLog('🔐 [Manager] Re-authenticating with current token');
    return this.authenticate(token);
  }

  /**
   * Start keepalive timer
   */
  startKeepalive() {
    this.stopKeepalive();
    this.keepaliveTimer = setInterval(() => {
      if (this.connected && this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.send({ type: 'ping' });
        devLog('🏓 [Manager] Keepalive ping sent');
      }
    }, this.keepaliveInterval);
  }

  /**
   * Stop keepalive timer
   */
  stopKeepalive() {
    if (this.keepaliveTimer) {
      clearInterval(this.keepaliveTimer);
      this.keepaliveTimer = null;
    }
  }

  /**
   * Close WebSocket connection (hard close, no debounce)
   */
  close() {
    // Clear timers
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.closeDebounceTimer) {
      clearTimeout(this.closeDebounceTimer);
      this.closeDebounceTimer = null;
    }
    this.stopKeepalive();

    // Close socket
    if (this.ws) {
      devLog('🔌 [Manager] Closing WebSocket');
      this.ws.close(1000, 'Manager close');
      this.ws = null;
    }

    this.connected = false;
    this.authenticated = false;
    this.authenticating = false;
    this.clientId = null;
    this.reconnectAttempts = 0;
  }

  /**
   * Get connection status
   */
  isConnected() {
    return this.connected && this.ws && this.ws.readyState === WebSocket.OPEN;
  }

  /**
   * Get authentication status
   */
  isAuthenticated() {
    return this.authenticated;
  }

  /**
   * Get current subscriber count
   */
  getSubscriberCount() {
    return this.subscribers;
  }

  /**
   * Get client ID (from server's init message)
   */
  getClientId() {
    return this.clientId;
  }
}

// Export singleton instance
export const websocketManager = new WebSocketManager();
export default websocketManager;

